import Link, { LinkProps } from 'next/link'
import { useTranslations } from 'next-intl'
import React, { useMemo } from 'react'

import { Button, ButtonProps } from '@/components/ui/button'

import { useAuth } from '@/hooks/use-auth'
import { useBillingDialogStore } from '@/stores/billing-dialog.store'

interface ManagePlanButtonProps {
  renderAppSumoLink?: (_props: { href: string; target: string; text: string }) => React.ReactNode
  appSumoButtonProps?: Omit<ButtonProps, 'onClick' | 'asChild' | 'children'>
  appSumoLinkProps?: Omit<LinkProps, 'href'>
  renderAppSumoLinkChildren?: () => React.ReactNode
  renderAppSumoFooter?: () => React.ReactNode
  billingDialogButtonProps?: Omit<ButtonProps, 'onClick' | 'children'>
  renderBillingDialogTrigger?: (_props: { onClick: () => void; text: string }) => React.ReactNode
  renderBillingDialogChildren?: () => React.ReactNode
  renderNonAppSumoFooter?: () => React.ReactNode
}
export const ManagePlanButton = ({
  renderAppSumoLink,
  appSumoButtonProps,
  appSumoLinkProps,
  renderAppSumoLinkChildren,
  renderAppSumoFooter,
  renderBillingDialogTrigger,
  billingDialogButtonProps,
  renderBillingDialogChildren,
  renderNonAppSumoFooter,
}: ManagePlanButtonProps) => {
  const tUserPreferences = useTranslations('UserPreferences')
  const { openDialog } = useBillingDialogStore()
  const { session } = useAuth()
  const isOrgAppSumoPlan = !!session?.user?.activeOrganization?.appSumoTierId
  const shouldShowAppSumoChangePlanButton = useMemo(
    () => isOrgAppSumoPlan && process.env.NEXT_PUBLIC_APP_SUMO_ENABLE_CHANGE_PLAN?.toLowerCase() === 'true',
    [isOrgAppSumoPlan],
  )
  const appSumoChangePlanUrl = useMemo(() => process.env.NEXT_PUBLIC_APP_SUMO_CHANGE_PLAN_URL!, [])

  return (
    <>
      {shouldShowAppSumoChangePlanButton ? (
        <>
          {renderAppSumoLink ? (
            renderAppSumoLink({
              href: appSumoChangePlanUrl,
              target: '_blank',
              text: tUserPreferences('usage.change_appsumo_plan'),
            })
          ) : (
            <Button asChild variant="default" {...appSumoButtonProps}>
              <Link href={appSumoChangePlanUrl} target="_blank" {...appSumoLinkProps}>
                {renderAppSumoLinkChildren?.() ?? tUserPreferences('usage.change_appsumo_plan')}
              </Link>
            </Button>
          )}
          {renderAppSumoFooter?.()}
        </>
      ) : (
        <>
          {renderBillingDialogTrigger ? (
            renderBillingDialogTrigger({ onClick: openDialog, text: tUserPreferences('usage.btnStorage') })
          ) : (
            <Button variant="default" onClick={openDialog} {...billingDialogButtonProps}>
              {renderBillingDialogChildren?.() ?? tUserPreferences('usage.btnStorage')}
            </Button>
          )}
          {renderNonAppSumoFooter?.()}
        </>
      )}
    </>
  )
}
